import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { Globe2 } from "lucide-react";
import { JOURNEY } from "../lib/content";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Compact map card — designed to slot into the Strengths 4-col grid (spans 2 cols on lg+).
const JourneyMapCard = () => {
  const [active, setActive] = useState(null);

  return (
    <div
      data-testid="journey-mini-card"
      className="reveal sm:col-span-2 lg:col-span-2 relative rounded-3xl overflow-hidden p-5 sm:p-6 bg-ink text-alabaster flex flex-col h-full"
      style={{ transitionDelay: "420ms" }}
    >
      {/* Decorative glow */}
      <div
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(245,187,31,0.22), transparent 60%)",
        }}
        aria-hidden
      />
      <div
        className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(92,132,112,0.25), transparent 60%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 dot-grid opacity-[0.08] pointer-events-none" aria-hidden />

      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-terracotta text-ink flex items-center justify-center">
            <Globe2 size={22} strokeWidth={1.8} />
          </span>
          <div>
            <div className="eyebrow !text-terracotta-soft">My Journey</div>
            <div className="font-display text-xl font-bold leading-tight">
              {JOURNEY.length} Countries · 4 Continents
            </div>
          </div>
        </div>

        {/* Map — fills the card */}
        <div className="relative mt-4 flex-1 w-full">
          {active && (
            <div
              data-testid="journey-mini-tooltip"
              className="absolute top-2 right-2 z-10 bg-alabaster/95 text-ink rounded-2xl px-4 py-2 max-w-[260px] shadow-xl pointer-events-none"
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: active.isHome ? "#284A3B" : "#F5BB1F" }}
                />
                <span className="text-[10px] font-display font-bold uppercase tracking-widest text-terracotta">
                  {active.isHome ? "Home" : "Visited"}
                </span>
              </div>
              <div className="font-display font-bold text-sm mt-0.5">
                {active.name}
              </div>
              <div className="text-[11px] text-ink-soft mt-0.5 leading-snug">
                {active.purpose}
              </div>
            </div>
          )}
          <ComposableMap
            projectionConfig={{ scale: 200 }}
            projection="geoEqualEarth"
            className="w-full h-full"
            style={{ width: "100%", height: "100%" }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: "#2A2F2C",
                        stroke: "#1C201E",
                        strokeWidth: 0.4,
                        outline: "none",
                      },
                      hover: { fill: "#3A4039", outline: "none" },
                      pressed: { fill: "#3A4039", outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {JOURNEY.map((j) => (
              <Marker key={j.id} coordinates={j.coords}>
                <g
                  data-testid={`journey-mini-pin-${j.id}`}
                  className="cursor-pointer"
                  onMouseEnter={() => setActive(j)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(j)}
                >
                  <circle
                    r={j.isHome ? 14 : 10}
                    fill={j.isHome ? "#284A3B" : "#F5BB1F"}
                    fillOpacity={0.2}
                  />
                  <circle
                    r={j.isHome ? 6 : 5}
                    fill={j.isHome ? "#284A3B" : "#F5BB1F"}
                    stroke="#FAF8F5"
                    strokeWidth={1.4}
                  />
                </g>
              </Marker>
            ))}
          </ComposableMap>

          {/* Country chips overlay — sits on top of map, no extra vertical space */}
          <div className="absolute left-2 right-2 bottom-2 flex flex-wrap gap-1.5 max-h-[42%] overflow-y-auto pr-1">
            {JOURNEY.map((j) => (
              <button
                key={j.id}
                data-testid={`journey-mini-chip-${j.id}`}
                onMouseEnter={() => setActive(j)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(j)}
                onClick={() => setActive(j)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-display font-semibold tracking-wide transition-all backdrop-blur-sm ${
                  active && active.id === j.id
                    ? "border-terracotta bg-terracotta text-ink"
                    : "border-alabaster/20 bg-ink/60 text-alabaster/85 hover:border-terracotta-soft hover:text-alabaster"
                }`}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: j.isHome ? "#5C8470" : "#F5BB1F" }}
                />
                {j.name}
                {j.isHome && (
                  <span className="text-[8px] tracking-widest uppercase ml-0.5 text-emerald2-soft">
                    Home
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyMapCard;
